import React, { useState, useMemo } from 'react';
import { Button } from '../../../../components/ui/button'; 
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '../../../../components/ui/dialog'; 
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select'; 
import { ScrollArea } from '../../../../components/ui/scroll-area'; 
import { PlusCircle, XCircle, Save, ExternalLink, AlertTriangle } from 'lucide-react';
import { CategorySearch } from '../CategorySearch'; 

export default function ConditionModal({
  triggerQuestion,
  allQuestions, 
  onClose,      
  onSave,       
  canBeConditional,
  locale 
}) {
  // Initialize state directly. The DB sends string keys (e.g., "18990").
  const [logic, setLogic] = useState(triggerQuestion.conditional_logic || {});

  const isOptionSaved = (opt) => {
    return Number.isInteger(opt.id);
  };

  const handleChange = (optionId, conditionIndex, field, value) => {
    // Cast optionId to string to match JSON keys
    const safeOptionId = String(optionId);
    
    setLogic(prevLogic => {
      const conditions = prevLogic[safeOptionId] ? [...prevLogic[safeOptionId]] : [];
      
      if (conditions[conditionIndex]) {
        let updated = { ...conditions[conditionIndex], [field]: value };

        if (field === 'action') {
            if (value === 'redirect') {
                delete updated.question; 
                updated.category_id = null;
                updated.category_name = '';
            } else {
                delete updated.category_id;
                delete updated.category_name;
                updated.question = ''; 
            }
        }
        conditions[conditionIndex] = updated;
      }
      return { ...prevLogic, [safeOptionId]: conditions };
    });
  };

  const handleCategorySelect = (optionId, conditionIndex, category) => {
    const safeOptionId = String(optionId);
    setLogic(prevLogic => {
        const conditions = prevLogic[safeOptionId] ? [...prevLogic[safeOptionId]] : [];
        if (conditions[conditionIndex]) {
            conditions[conditionIndex] = { 
                ...conditions[conditionIndex], 
                category_id: category.id,
                category_name: category.name
            };
        }
        return { ...prevLogic, [safeOptionId]: conditions };
    });
  };

  const handleAddCondition = (optionId) => {
    const safeOptionId = String(optionId);
    setLogic(prevLogic => {
      const conditions = prevLogic[safeOptionId] ? [...prevLogic[safeOptionId]] : [];
      return {
        ...prevLogic,
        [safeOptionId]: [...conditions, { action: 'show', question: '' }], 
      };
    });
  };

  const handleRemoveCondition = (optionId, conditionIndex) => {
    const safeOptionId = String(optionId);
    setLogic(prevLogic => {
      const conditions = prevLogic[safeOptionId] ? [...prevLogic[safeOptionId]] : [];
      if (conditions[conditionIndex]) {
        conditions.splice(conditionIndex, 1);
      }
      return { ...prevLogic, [safeOptionId]: conditions };
    });
  };

  const handleSaveChanges = () => {
    // Clean up empty arrays before saving
    const cleanedLogic = {};
    for (const key in logic) {
      if (logic[key] && logic[key].length > 0) {
        cleanedLogic[key] = logic[key];
      }
    }
    
    onSave({ logic: cleanedLogic, isEnabled: Object.keys(cleanedLogic).length > 0 }); 
  };

  const isSaveDisabled = useMemo(() => {
    for (const key in logic) {
      if (logic[key]) {
        for (const condition of logic[key]) {
          if (condition.action === 'redirect') {
             if (!condition.category_id) return true;
          } else {
             if (!condition.question) return true;
          }
        }
      }
    }
    return false;
  }, [logic]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl"> 
        <DialogHeader>
          <DialogTitle>Setup Logic for: <span className="font-semibold">{triggerQuestion.label}</span></DialogTitle>
          <DialogDescription>
            Show/Hide questions or Redirect to a specific category based on the answer.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] p-1 pr-4">
          <div className="space-y-6 py-4">
            {triggerQuestion.options && triggerQuestion.options.length > 0 ? (
              triggerQuestion.options.map(opt => {
                // Always look up logic using the stringified ID
                const safeOptionId = String(opt.id);
                const currentConditions = logic[safeOptionId] || [];

                return (
                <div key={opt.id || opt.temp_id} className={`p-4 border rounded-md shadow-sm ${isOptionSaved(opt) ? 'bg-card border-border' : 'bg-muted/30 border-dashed border-yellow-300'}`}>
                  
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-md font-medium text-foreground">
                       Option: <span className="font-semibold text-primary">{opt.label}</span>
                    </h3>
                    {!isOptionSaved(opt) && (
                       <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded">
                         <AlertTriangle className="h-3 w-3"/> Save changes to enable logic
                       </span>
                    )}
                  </div>

                  {isOptionSaved(opt) ? (
                    <>
                        {currentConditions.length === 0 && (
                            <p className="text-sm text-muted-foreground mb-2">No conditions defined.</p>
                        )}

                        {currentConditions.map((condition, idx) => (
                            <div key={idx} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-3 p-3 bg-background rounded-md border border-border">
                            
                            <Select
                                value={condition.action}
                                onValueChange={(value) => handleChange(safeOptionId, idx, 'action', value)}
                            >
                                <SelectTrigger className="w-[160px]">
                                <SelectValue placeholder="Action" />
                                </SelectTrigger>
                                <SelectContent>
                                <SelectItem value="show">Show Question</SelectItem>
                                <SelectItem value="hide">Hide Question</SelectItem>
                                <SelectItem value="redirect">Redirect Category</SelectItem>
                                </SelectContent>
                            </Select>

                            {condition.action === 'redirect' ? (
                                <div className="flex-1 w-full min-w-[250px]">
                                    {condition.category_id ? (
                                        <div className="flex items-center justify-between border rounded px-3 py-2 bg-muted/20">
                                            <span className="text-sm font-medium flex items-center gap-2">
                                                <ExternalLink className="w-4 h-4 text-blue-500"/>
                                                Redirect to: {condition.category_name || `Category ${condition.category_id}`}
                                            </span>
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                className="h-6 text-xs"
                                                onClick={() => handleChange(safeOptionId, idx, 'category_id', null)}
                                            >
                                                Change
                                            </Button>
                                        </div>
                                    ) : (
                                        <CategorySearch 
                                            locale={locale || 'IN'}
                                            onSelect={(cat) => handleCategorySelect(safeOptionId, idx, cat)}
                                            placeholder="Search target category..."
                                        />
                                    )}
                                </div>
                            ) : (
                                <Select
                                    value={condition.question ? String(condition.question) : undefined} 
                                    onValueChange={(value) => handleChange(safeOptionId, idx, 'question', Number(value))}
                                >
                                    <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Select target question..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                    <SelectItem value="None" disabled>Select Target Question...</SelectItem>
                                    {allQuestions
                                        .filter(q =>
                                        q.id !== triggerQuestion.id && 
                                        q.order_index > triggerQuestion.order_index && 
                                        (
                                            condition.action === 'hide' || 
                                            (
                                            condition.action === 'show' &&
                                            canBeConditional(q.question_type) && 
                                            q.conditional === true 
                                            )
                                        )
                                        )
                                        .map(q => (
                                        <SelectItem key={q.id} value={String(q.id)}>
                                            {q.label} <span className="text-xs text-muted-foreground">(ID: {q.id})</span>
                                        </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}

                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleRemoveCondition(safeOptionId, idx)}
                            >
                                <XCircle className="h-5 w-5" />
                            </Button>
                            </div>
                        ))}

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddCondition(safeOptionId)}
                            className="mt-2"
                        >
                            <PlusCircle className="mr-2 h-4 w-4" /> Add Condition
                        </Button>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground bg-muted/20 p-2 rounded italic">
                        Conditional logic cannot be added to unsaved options. Please save the question first.
                    </div>
                  )}
                </div>
              )})
            ) : (
              <p className="text-muted-foreground text-center py-4">
                This question type does not support option-based branching.
              </p>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="mt-6">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSaveChanges} disabled={isSaveDisabled}>
            <Save className="mr-2 h-4 w-4" /> Save Logic
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}